/**
 * The launch funnel, summarised from admin_activation_funnel rows (one per
 * workspace; each milestone is the earliest row of its kind, null if not reached).
 */
export interface ActivationRow {
  account_id: string;
  workspace_name: string | null;
  edition_key: string | null;
  paid: boolean;
  created_at: string;
  first_asset_at: string | null;
  first_inspection_started_at: string | null;
  first_inspection_completed_at: string | null;
  first_finding_at: string | null;
  first_job_at: string | null;
  first_job_completed_at: string | null;
  first_collaborator_at: string | null;
  first_contractor_job_at: string | null;
  first_maintenance_plan_at: string | null;
  last_active_at: string | null;
  active_assets: number;
}

export type Milestone = Exclude<keyof ActivationRow, 'account_id' | 'workspace_name' | 'edition_key' | 'paid' | 'created_at' | 'last_active_at' | 'active_assets'>;

export const FUNNEL: { key: Milestone; label: string }[] = [
  { key: 'first_asset_at', label: 'First asset' },
  { key: 'first_inspection_started_at', label: 'First inspection started' },
  { key: 'first_inspection_completed_at', label: 'First inspection completed' },
  { key: 'first_finding_at', label: 'First finding' },
  { key: 'first_job_at', label: 'First job' },
  { key: 'first_job_completed_at', label: 'First work completed' },
];

export const SUPPORTING: { key: Milestone; label: string }[] = [
  { key: 'first_collaborator_at', label: 'First collaborator' },
  { key: 'first_contractor_job_at', label: 'First contractor job' },
  { key: 'first_maintenance_plan_at', label: 'First maintenance plan' },
];

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export interface MilestoneSummary { label: string; reached: number; medianHours: number | null }

/** How many workspaces reached a milestone, and the median time from signup to it. */
export function summariseMilestone(rows: ActivationRow[], key: Milestone, label: string): MilestoneSummary {
  const hours = rows
    .filter((r) => r[key])
    .map((r) => (Date.parse(r[key] as string) - Date.parse(r.created_at)) / HOUR);
  return { label, reached: hours.length, medianHours: median(hours) };
}

/**
 * Day-N return: of the workspaces old enough to be asked, how many did something
 * on or after day N. "Did something" is the last asset / inspection / job row.
 */
export function returnedAfter(rows: ActivationRow[], days: number, now = Date.now()): { eligible: number; returned: number } {
  const eligible = rows.filter((r) => now - Date.parse(r.created_at) >= days * DAY);
  const returned = eligible.filter(
    (r) => r.last_active_at && Date.parse(r.last_active_at) - Date.parse(r.created_at) >= days * DAY
  );
  return { eligible: eligible.length, returned: returned.length };
}
